import './promo-slider.scss';

import type React from 'react';
import { useCallback, useRef, useState } from 'react';

import { twa } from '@/shared/lib/telegram/twa';

import { PromoBanner } from './PromoBanner';
import type { PromoBannerData } from './types';

type Props = {
  banners: PromoBannerData[];
};

const DRAG_THRESHOLD = 40;

export function PromoSlider({ banners }: Props) {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState<1 | -1>(1);
  const dragStartX = useRef<number | null>(null);

  const goTo = useCallback(
    (index: number) => {
      if (index === current) return;
      setDirection(index > current ? 1 : -1);
      setCurrent(index);
      twa.hapticFeedback.selectionChanged();
    },
    [current],
  );

  const handleDragStart = useCallback((x: number) => {
    dragStartX.current = x;
  }, []);

  const handleDragEnd = useCallback(
    (x: number) => {
      if (dragStartX.current === null) return;
      const delta = dragStartX.current - x;
      dragStartX.current = null;
      if (Math.abs(delta) < DRAG_THRESHOLD) return;
      if (delta > 0 && current < banners.length - 1) goTo(current + 1);
      if (delta < 0 && current > 0) goTo(current - 1);
    },
    [current, banners.length, goTo],
  );

  if (!banners.length) return null;

  return (
    <div className="promo-slider">
      <div
        className="promo-slider__track"
        onMouseDown={(e) => handleDragStart(e.clientX)}
        onMouseUp={(e) => handleDragEnd(e.clientX)}
        onTouchStart={(e) => handleDragStart(e.touches[0].clientX)}
        onTouchEnd={(e) => handleDragEnd(e.changedTouches[0].clientX)}>
        <div
          key={banners[current].id}
          className="promo-slider__slide"
          style={{ '--slide-dir': direction } as React.CSSProperties}>
          <PromoBanner
            data={banners[current]}
            onClick={() => {
              twa.hapticFeedback.impactOccurred('light');
              banners[current].onClick?.();
            }}
          />
        </div>
      </div>

      {banners.length > 1 && (
        <div className="promo-slider__dots">
          {banners.map((b, i) => (
            <button
              key={b.id}
              className={`promo-slider__dot${i === current ? ' promo-slider__dot--active' : ''}`}
              onClick={() => goTo(i)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
